import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import classnames from 'classnames';
import mixpanel from 'mixpanel-browser';

import { addError } from '@actions/appState';
import { loginRequest, loginSuccess } from '@actions/auth';
import { fetchProductLayout } from '@actions/productLayout';

import { getLandingPagePartialURL } from '@utils/AppUtil';
import { authService, getCurrentAgency, getUserProfile, isLoggedIn } from '@utils/AuthUtil';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import { track } from '@utils/MixPanelUtils';
import { consumePostLoginRedirectUrl } from '@utils/PostLoginRedirectUtil';

// tslint:disable-next-line: prettier
const USER_DEACTIVATED_ERROR = "User's account has been deactivated from Cyberdyne.";

const USER_HAVE_NO_PRIVELEGES = 'User account does not have necessary privileges to log in.';

interface ILoginCallbackProps extends RouteComponentProps<{}> {
  // from redux
  userProfile: IUserProfile;
  addError: TAddError;
  loginSuccess: TLoginSuccess;
  loginRequest: TLoginRequest;
  serverError: any;
  fetchProductLayout?: TFetchProductLayout;
}

class LoginCallback extends React.PureComponent<ILoginCallbackProps> {
  componentDidMount() {
    this.handleAuthetication();
  }

  componentDidUpdate(prevProps: ILoginCallbackProps) {
    if (prevProps.userProfile !== this.props.userProfile && !!this.props.userProfile) {
      mixpanel.identify(getUserProfile().email);

      track('USER_LOGGED_IN', {
        'Device Details': getFormattedDeviceDetails(),
      });
      this.redirectUser(this.props.userProfile);
    }
  }

  redirectUser = async (userProfile: IUserProfile) => {
    await this.props.loginSuccess(userProfile);
    const landingPage = getLandingPagePartialURL(userProfile);
    const currentAgency = getCurrentAgency();
    const url = consumePostLoginRedirectUrl();
    if (url) {
      this.props.history.push(url);
    } else if (landingPage !== '/') {
      this.props.history.push(`/agencies/${currentAgency.id}/${landingPage}`);
    } else {
      const errMsg = 'User account does not have necessary privileges to log in.';
      this.logoutUser(errMsg);
    }
  };

  // check if the user belongs to the agency
  validateAgency = async () => {
    const currentAgency = getCurrentAgency();
    const { idToken } = await authService.tokenManager.getTokens();

    return currentAgency.id === idToken.claims.agency_id;
  };

  logoutUser = async (state?: string) => {
    const options: any = {};
    if (state) {
      options.state = state;
    }
    await authService.signOut(options);
  };

  handleAuthetication = async () => {
    const currentAgency = getCurrentAgency();
    try {
      const tokenResponse = await authService.token.parseFromUrl();
      authService.tokenManager.setTokens(tokenResponse.tokens);
    } catch (error) {
      // User tried to access page without authorization code
      this.props.history.push(`/agencies/${currentAgency.id}/login`);
    }
    if (!isLoggedIn()) {
      const validAgency = await this.validateAgency();

      if (validAgency) {
        const idToken = authService.getIdToken();
        const accessToken = authService.getAccessToken();
        const res = await this.props.loginRequest('', '', currentAgency.id, idToken, accessToken);

        if (res.error) {
          if (this.props.serverError && this.props.serverError === USER_DEACTIVATED_ERROR) {
            this.logoutUser(LOGIN_ERROR_STATE.OAUTH_INACTIVE_USER_ERROR_STATE);
          } else if (this.props.serverError && this.props.serverError === USER_HAVE_NO_PRIVELEGES) {
            this.logoutUser(LOGIN_ERROR_STATE.OAUTH_USER_HAVE_NO_PRIVELEGES);
          } else {
            this.logoutUser(LOGIN_ERROR_STATE.OAUTH_SILENT_ERROR_STATE);
          }
        }
      } else {
        const error =
          // tslint:disable-next-line: max-line-length
          'You are not authorized to access this agency, please check with your agency administrator to request access.';
        this.logoutUser(error);
      }
    }
  };

  render() {
    return <h1 className={classnames('redirection-message')}>Wait while we redirect you to your agency</h1>;
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  userProfile: state.auth.userProfile,
  serverError: state.auth.serverError,
});

const mapDispatchToProps = (dispatch: any) => ({
  loginRequest: (email: string, password: string, agencyId: number, idToken?: string, accessToken?: string) =>
    dispatch(loginRequest(email, password, agencyId, idToken, accessToken)),
  loginSuccess: (userProfile: IUserProfile) => dispatch(loginSuccess(userProfile)),
  addError: (errorMessage: string) => dispatch(addError(errorMessage)),
  fetchProductLayout: () => dispatch(fetchProductLayout()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LoginCallback));
